import React, { useCallback, useEffect, useState } from 'react';
import { MapNode } from '..';
import { outcomeLayoutElements } from './OutcomeMapLayout';
import { Outcome } from 'types';
import ReactFlow, {
  Background,
  ConnectionLineType,
  Controls,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from 'reactflow';
import theme from 'theme';
import SmallPlanNode from './SmallPlanNode';
import OutcomeNode from './OutcomeNode';

const nodeTypes = {
  smallPlan: SmallPlanNode,
  outcome: OutcomeNode,
};

interface Props {
  initialTree: Record<string, MapNode>;
  rootId: string;
  fullScreenEnabled?: boolean;
}

function OutcomeMap(props: Props) {
  const { initialTree, rootId, fullScreenEnabled } = props;
  const [tree, setTree] = useState(initialTree); // Store tree structure
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const reactFlowInstance = useReactFlow();

  const addDependentOutcomes = useCallback(
    (nodeId, outcomes) => {
      setTree((prevTree) => {
        const prevNode = prevTree[nodeId];
        return {
          ...prevTree,
          [nodeId]: { ...prevNode, meta: { ...prevNode.meta, dependentOutcomes: outcomes } },
        };
      });
    },
    [setTree],
  );

  const toggleExpand = useCallback(
    (nodeId, expanded) => {
      setTree((prevTree) => {
        const prevNode = prevTree[nodeId];
        const dependentOutcomes: Outcome[] = prevNode.meta.dependentOutcomes;
        if (expanded) {
          const dependentNodes: Record<string, MapNode> = dependentOutcomes.reduce((acc, dependent) => {
            acc[dependent.id] = {
              id: dependent.id,
              meta: { expanded: false, contentExpanded: false, outcome: dependent, children: [] },
              type: 'outcome',
              name: dependent.title,
              children: [],
              parents: [nodeId],
            };
            return acc;
          }, {} as Record<string, MapNode>);
          const children = [...dependentOutcomes.map((child) => child.id)];
          const newTree = {
            ...prevTree,
            ...dependentNodes,
            [nodeId]: { ...prevNode, children, meta: { ...prevNode.meta, expanded } },
          };
          return newTree;
        } else {
          const newTree = {
            ...prevTree,
            [nodeId]: { ...prevNode, children: [], meta: { ...prevNode.meta, expanded } },
          };
          return newTree;
        }
      });
    },
    [setTree],
  );

  const saveContentExpanded = useCallback(
    (nodeId, height, contentExpanded: boolean) => {
      setTree((prevTree) => {
        const prev = prevTree[nodeId];
        const newMeta = { ...prev.meta, loadedHeight: height, contentExpanded };
        return {
          ...prevTree,
          [nodeId]: {
            ...prev,
            meta: newMeta,
          },
        };
      });
    },
    [setTree],
  );

  const saveOutcomeNodeHeight = useCallback(
    (nodeId, height) => {
      setTree((prevTree) => {
        const prev = prevTree[nodeId];
        const newMeta = { ...prev.meta, outcomeHeight: height };
        return {
          ...prevTree,
          [nodeId]: {
            ...prev,
            meta: newMeta,
          },
        };
      });
    },
    [setTree],
  );

  useEffect(() => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = outcomeLayoutElements(
      tree,
      rootId,
      addDependentOutcomes,
      toggleExpand,
      saveContentExpanded,
      saveOutcomeNodeHeight,
    );

    setNodes([...layoutedNodes]);
    setEdges([...layoutedEdges]);
  }, [
    tree,
    addDependentOutcomes,
    toggleExpand,
    saveContentExpanded,
    saveOutcomeNodeHeight,
    reactFlowInstance,
    rootId,
    setEdges,
    setNodes,
  ]); // Re-run when tree updates

  useEffect(() => {
    const timer = setTimeout(() => {
      const zoom = fullScreenEnabled ? 0.7 : 0.4;
      reactFlowInstance.fitView({ duration: 500, maxZoom: zoom, minZoom: zoom });
    }, 300); // Adjust the delay (in milliseconds) as needed

    return () => clearTimeout(timer);
  }, [reactFlowInstance, fullScreenEnabled]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      connectionLineType={ConnectionLineType.SmoothStep}
      fitView
      nodeTypes={nodeTypes}
      snapToGrid={true}
      minZoom={0.1}
      maxZoom={2}
      style={{ background: theme.colors.N3 }}
      proOptions={{ account: 'paid-pro', hideAttribution: true }}
      defaultViewport={{
        x: 0,
        y: 0,
        zoom: 0.8,
      }}
    >
      <Background color={theme.colors.N20} size={2} />
      <Controls />
    </ReactFlow>
  );
}

export default React.memo(OutcomeMap);
